import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useGetAvailability } from '../../api/generated/endpoints/availability/availability'
import {
  getListMyTournamentEntriesQueryKey,
  useBookTournamentRound,
  useGetTournament,
} from '../../api/generated/endpoints/tournaments/tournaments'
import { HourAccordion } from '../../components/HourAccordion'
import { apiMessage } from '../../lib/api-error'
import { isLocalDate } from '../../lib/slots'
import { addDays, formatDayLong, formatWeekday, dayOfMonth } from '../../lib/time'

/**
 * Choosing when to play your tournament round.
 *
 * A separate screen from `/book` rather than a mode of it. The two look alike
 * and are not: this one has no price, no payment, no "any bay is fine by me"
 * default worth arguing about, and its day strip is the coach's published
 * playing period rather than the rolling three-day horizon. Folding both into
 * one component would mean a prop that silently changes five behaviours.
 *
 * The booking underneath IS the same thing — an ordinary row, protected by the
 * same exclusion constraint. Only the rules about who may create it differ.
 */
export const Route = createFileRoute('/_authed/round/$slug')({
  validateSearch: (search: Record<string, unknown>): { date?: string } => ({
    date: isLocalDate(search.date) ? search.date : undefined,
  }),
  component: RoundPage,
})

function RoundPage() {
  const { slug } = Route.useParams()
  const { date } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const [openHour, setOpenHour] = useState<string | null>(null)

  const tournament = useGetTournament(slug)
  const day = date ?? tournament.data?.plays_from

  const availability = useGetAvailability(
    { date: day, tournament: slug },
    { query: { enabled: Boolean(day), staleTime: 15_000 } },
  )

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getListMyTournamentEntriesQueryKey() }),
      availability.refetch(),
    ])

  const book = useBookTournamentRound({
    mutation: {
      retry: false,
      onSuccess: async () => {
        await refresh()
        void navigate({ to: '/tournaments' })
      },
    },
  })

  if (tournament.isPending) return <p className="text-slate-600">Cargando…</p>
  if (tournament.isError || !day) {
    return (
      <p role="alert" className="text-red-600">
        No encontramos ese torneo.
      </p>
    )
  }

  const event = tournament.data
  const days = periodDays(event.plays_from, event.plays_until)
  const closed = new Set(availability.data?.closed_dates ?? [])

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Elegir horario</h1>
        <p className="mt-1 text-slate-600">
          {event.name}
          {event.gspro_course && <> &middot; {event.gspro_course}</>}
        </p>
        <p className="mt-1 text-sm text-slate-600">
          {days.length === 1
            ? `Se juega el ${formatDayLong(event.plays_from)}.`
            : `Se puede jugar entre el ${formatDayLong(event.plays_from)} y el ${formatDayLong(
                event.plays_until,
              )}.`}{' '}
          La hora de simulador ya está incluida en la inscripción.
        </p>
      </div>

      {/* The published period, not the three-day horizon. Edge to edge and
          scrollable, the same trick DayStrip uses -- a month-long tournament is
          thirty targets and they must not shrink to fit. */}
      <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1">
        {days.map((option) => {
          const selected = option === day
          const shut = closed.has(option)
          return (
            <Link
              key={option}
              to="/round/$slug"
              params={{ slug }}
              search={{ date: option }}
              aria-current={selected ? 'date' : undefined}
              aria-disabled={shut || undefined}
              className={[
                'flex min-h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg border',
                shut ? 'border-slate-200 bg-slate-100 text-slate-400' : 'border-slate-300 bg-white',
                selected ? 'border-slate-900 bg-slate-900 text-white' : '',
              ].join(' ')}
            >
              <span className="text-xs uppercase">{formatWeekday(option)}</span>
              <span className="text-lg font-semibold tabular-nums">{dayOfMonth(option)}</span>
            </Link>
          )
        })}
      </div>

      {availability.isPending && <p className="text-slate-600">Cargando…</p>}

      {availability.data && closed.has(day) ? (
        <p
          role="status"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          El local está cerrado ese día. Hay que elegir otro.
        </p>
      ) : (
        availability.data && (
          <HourAccordion
            // Remount on a day change so the open hour re-derives rather than
            // being synced with an effect.
            key={day}
            starts={availability.data.starts}
            slotMinutes={availability.data.slot_minutes}
            openHour={openHour}
            onOpenHour={setOpenHour}
            onSelect={(start) => book.mutate({ slug, data: { date: day, start, bay_id: null } })}
          />
        )
      )}

      {book.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiMessage(book.error, 'No se pudo reservar ese horario.')}
        </p>
      )}

      <Link
        to="/tournaments"
        className="inline-flex min-h-12 items-center text-slate-700 underline"
      >
        Volver a los torneos
      </Link>
    </div>
  )
}

/** Every local date from `from` to `to`, inclusive at both ends. */
function periodDays(from: string, to: string): string[] {
  const days: string[] = []
  for (let day = from; day <= to; day = addDays(day, 1)) {
    days.push(day)
    // A tournament period is a coach's decision, not a validated range, and a
    // typo of 2027 for 2026 should not hang the browser.
    if (days.length >= 62) break
  }
  return days
}
